import type React from "react";

import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { MUISwitch } from "shared/atoms/MUI/MUISwitch";

import { ProfileRow } from "../shared/ProfileRow";

export const AvailableToSubstituteSwitch: React.FC = () => {
  const { profile, setProfile } = useProfileContext();
  const { t } = useTranslation();
  const putTherapistMutation = usePutTherapistMutation();

  invariant(profile);

  const isAvailableToSubstitute = Boolean(profile.therapist_profile.available_to_substitute);
  const isEnabled = isAvailableToSubstitute;

  const onSubmit = (availableToSubstitute: boolean) => {
    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        available_to_substitute: availableToSubstitute,
      },
    };

    // Optimistically update the profile for immediate UI feedback.
    // Then onSuccess we update the profile with "real" server state.
    setProfile({
      ...profile,
      therapist_profile: {
        ...profile.therapist_profile,
        ...data.therapist_profile,
      },
    });

    const variables = { data, therapistId };
    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);
      },
    });
  };

  return (
    <ProfileRow
      label={t("therapist_settings.available_to_substitute.title")}
      labelTestId="available-to-substitute-header"
      tooltip={t("therapist_settings.available_to_substitute.description")}
      value={
        <MUISwitch
          data-testid="available-to-substitute-switch"
          checked={isEnabled}
          onChange={() => {
            onSubmit(!isAvailableToSubstitute);
          }}
        />
      }
      errorMessage={putTherapistMutation.isError ? t("errors.generic") : undefined}
      dataTestId="available-to-substitute"
    />
  );
};
